export interface CallCenterPreferences {
	ringSoundEnabled: boolean;
	autoOpenCallBar: boolean;
	inputDeviceId: string;
	outputDeviceId: string;
}

export const CALL_CENTER_PREFERENCES_STORAGE_KEY = "kannact-call-center-preferences";

// Sentinel meaning "let the browser/OS pick". Matches the literal "default"
// deviceId Chromium exposes for its default output device, so it can be passed
// straight to device.audio.speakerDevices.set().
export const DEFAULT_AUDIO_DEVICE_ID = "default";

const DEFAULT_PREFERENCES: CallCenterPreferences = {
	ringSoundEnabled: true,
	autoOpenCallBar: true,
	inputDeviceId: DEFAULT_AUDIO_DEVICE_ID,
	outputDeviceId: DEFAULT_AUDIO_DEVICE_ID,
};

export const getCallCenterPreferences = (): CallCenterPreferences => {
	try {
		const stored = localStorage.getItem(CALL_CENTER_PREFERENCES_STORAGE_KEY);
		if (stored) {
			const parsed = JSON.parse(stored) as Partial<CallCenterPreferences>;
			// Validate field by field so a stale/corrupt entry degrades to defaults.
			return {
				ringSoundEnabled:
					typeof parsed.ringSoundEnabled === "boolean"
						? parsed.ringSoundEnabled
						: DEFAULT_PREFERENCES.ringSoundEnabled,
				autoOpenCallBar:
					typeof parsed.autoOpenCallBar === "boolean"
						? parsed.autoOpenCallBar
						: DEFAULT_PREFERENCES.autoOpenCallBar,
				inputDeviceId:
					typeof parsed.inputDeviceId === "string" && parsed.inputDeviceId
						? parsed.inputDeviceId
						: DEFAULT_AUDIO_DEVICE_ID,
				outputDeviceId:
					typeof parsed.outputDeviceId === "string" && parsed.outputDeviceId
						? parsed.outputDeviceId
						: DEFAULT_AUDIO_DEVICE_ID,
			};
		}
	} catch {
		// localStorage may be unavailable (private mode, blocked cookies) — ignore.
	}
	return { ...DEFAULT_PREFERENCES };
};

export const persistCallCenterPreferences = (preferences: CallCenterPreferences): void => {
	try {
		localStorage.setItem(CALL_CENTER_PREFERENCES_STORAGE_KEY, JSON.stringify(preferences));
	} catch {
		// Persistence is best-effort; the in-memory preferences still apply this session.
	}
};
